/** Zoom / fit helpers for the ownership graph viewport. */

export const GRAPH_MAX_UPSCALE = 1
export const GRAPH_ZOOM_STEP = 1.25
export const GRAPH_MIN_USER_ZOOM = 0.01
export const GRAPH_MAX_USER_ZOOM = 500
export const GRAPH_PAN_THRESHOLD_PX = 5

/** Target minimum effective scale (fitScale × userZoom) for very large graphs. */
export const GRAPH_MIN_EFFECTIVE_SCALE = 0.025

/** Visible viewport window (px) — does not grow/shrink with user zoom. */
export const VIEWPORT_DEFAULT_HEIGHT_PX = 560
export const VIEWPORT_MIN_HEIGHT_PX = 420

/** Never scale above 1:1 — small graphs stay at layout pixel size. */
export const GRAPH_ZOOM_TIP_MAX_EFFECTIVE_SCALE = 0.35

export function shouldShowGraphZoomTip(effectiveScale: number): boolean {
  return effectiveScale > 0 && effectiveScale < GRAPH_ZOOM_TIP_MAX_EFFECTIVE_SCALE
}

export function computeFitScale(containerWidth: number, svgWidth: number): number {
  if (svgWidth <= 0 || containerWidth <= 0) return GRAPH_MAX_UPSCALE
  return Math.min(GRAPH_MAX_UPSCALE, containerWidth / svgWidth)
}

export function computeEffectiveScale(fitScale: number, userZoom: number): number {
  return fitScale * userZoom
}

export function minUserZoomForFit(fitScale: number): number {
  if (fitScale <= 0) return GRAPH_MIN_USER_ZOOM
  return Math.min(GRAPH_MIN_USER_ZOOM, GRAPH_MIN_EFFECTIVE_SCALE / fitScale)
}

export function clampUserZoom(zoom: number, fitScale?: number): number {
  const floor = fitScale != null ? minUserZoomForFit(fitScale) : GRAPH_MIN_USER_ZOOM
  return Math.min(GRAPH_MAX_USER_ZOOM, Math.max(floor, zoom))
}

export function zoomInUserZoom(current: number, fitScale?: number): number {
  return clampUserZoom(current * GRAPH_ZOOM_STEP, fitScale)
}

export function zoomOutUserZoom(current: number, fitScale?: number): number {
  return clampUserZoom(current / GRAPH_ZOOM_STEP, fitScale)
}

/** Smooth wheel zoom — negative deltaY zooms in. */
export function applyWheelUserZoom(current: number, deltaY: number, fitScale?: number): number {
  const factor = Math.exp(-deltaY * 0.002)
  return clampUserZoom(current * factor, fitScale)
}

export interface ViewportPoint {
  x: number
  y: number
}

export function computeCenterOffset(containerSize: number, contentSize: number): number {
  return Math.max(0, (containerSize - contentSize) / 2)
}

/** Keep the graph point under `mouse` fixed while changing scale and centering. */
export function computePanForZoomAtPoint(
  mouse: ViewportPoint,
  pan: ViewportPoint,
  centerOffset: ViewportPoint,
  effectiveScale: number,
  newCenterOffset: ViewportPoint,
  newEffectiveScale: number,
): ViewportPoint {
  const gx = (mouse.x - centerOffset.x - pan.x) / effectiveScale
  const gy = (mouse.y - centerOffset.y - pan.y) / effectiveScale
  return {
    x: mouse.x - newCenterOffset.x - gx * newEffectiveScale,
    y: mouse.y - newCenterOffset.y - gy * newEffectiveScale,
  }
}

export function computeWheelZoomViewUpdate(params: {
  mouseX: number
  mouseY: number
  pan: ViewportPoint
  userZoom: number
  fitScale: number
  svgWidth: number
  svgHeight: number
  containerWidth: number
  containerHeight: number
  deltaY: number
}): { userZoom: number; pan: ViewportPoint } | null {
  const newUserZoom = applyWheelUserZoom(params.userZoom, params.deltaY, params.fitScale)
  if (newUserZoom === params.userZoom) return null

  const curEffective = computeEffectiveScale(params.fitScale, params.userZoom)
  const newEffective = computeEffectiveScale(params.fitScale, newUserZoom)
  const curContent = computeZoomedContentSize(params.svgWidth, params.svgHeight, curEffective)
  const newContent = computeZoomedContentSize(params.svgWidth, params.svgHeight, newEffective)
  const curCenter = {
    x: computeCenterOffset(params.containerWidth, curContent.width),
    y: computeCenterOffset(params.containerHeight, curContent.height),
  }
  const newCenter = {
    x: computeCenterOffset(params.containerWidth, newContent.width),
    y: computeCenterOffset(params.containerHeight, newContent.height),
  }

  return {
    userZoom: newUserZoom,
    pan: computePanForZoomAtPoint(
      { x: params.mouseX, y: params.mouseY },
      params.pan,
      curCenter,
      curEffective,
      newCenter,
      newEffective,
    ),
  }
}

/** Toolbar zoom in/out — anchor at the viewport frame centre. */
export function computeButtonZoomViewUpdate(params: {
  direction: 'in' | 'out'
  pan: ViewportPoint
  userZoom: number
  fitScale: number
  svgWidth: number
  svgHeight: number
  containerWidth: number
  containerHeight: number
}): { userZoom: number; pan: ViewportPoint } | null {
  const newUserZoom =
    params.direction === 'in'
      ? zoomInUserZoom(params.userZoom, params.fitScale)
      : zoomOutUserZoom(params.userZoom, params.fitScale)
  if (newUserZoom === params.userZoom) return null

  const curEffective = computeEffectiveScale(params.fitScale, params.userZoom)
  const newEffective = computeEffectiveScale(params.fitScale, newUserZoom)
  const curContent = computeZoomedContentSize(params.svgWidth, params.svgHeight, curEffective)
  const newContent = computeZoomedContentSize(params.svgWidth, params.svgHeight, newEffective)
  const curCenter = {
    x: computeCenterOffset(params.containerWidth, curContent.width),
    y: computeCenterOffset(params.containerHeight, curContent.height),
  }
  const newCenter = {
    x: computeCenterOffset(params.containerWidth, newContent.width),
    y: computeCenterOffset(params.containerHeight, newContent.height),
  }

  return {
    userZoom: newUserZoom,
    pan: computePanForZoomAtPoint(
      { x: params.containerWidth / 2, y: params.containerHeight / 2 },
      params.pan,
      curCenter,
      curEffective,
      newCenter,
      newEffective,
    ),
  }
}

/** Fit-sized layout dimensions (user zoom does not affect the viewport frame). */
export function computeFitDisplaySize(
  svgWidth: number,
  svgHeight: number,
  fitScale: number,
): { width: number; height: number } {
  return {
    width: svgWidth * fitScale,
    height: svgHeight * fitScale,
  }
}

/** Scrollable canvas extent when zoomed — may exceed the viewport frame. */
export function computeZoomedContentSize(
  svgWidth: number,
  svgHeight: number,
  effectiveScale: number,
): { width: number; height: number } {
  return {
    width: svgWidth * effectiveScale,
    height: svgHeight * effectiveScale,
  }
}
